import React, { useState, useMemo } from 'react'
import { Input } from '../ui/input'
import { Select } from 'antd'
import { Button } from '../ui/button'
import { Search, Filter, ChevronDown, X } from 'lucide-react'
import { StatusConfig } from '../../types/container'

interface NameOption {
  name: string
  shortName?: string
}

// Port / Warehouse filters match by id, not by name — a task points at a facility
// row, and short names are what dispatchers actually read on the Route column.
interface FacilityFilterOption {
  id: string
  name: string
  shortName?: string
}

interface SearchFiltersProps {
  searchTerm: string
  setSearchTerm: (value: string) => void
  selectedDriver: string
  setSelectedDriver: (value: string) => void
  selectedShipper: string
  setSelectedShipper: (value: string) => void
  selectedOrderingParty: string
  setSelectedOrderingParty: (value: string) => void
  importExportFilter: string
  setImportExportFilter: (value: string) => void
  destinationFilter: string
  setDestinationFilter: (value: string) => void
  portFilter: string
  setPortFilter: (value: string) => void
  selectedStatus: string
  setSelectedStatus: (value: string) => void
  lfdFilter: string
  setLfdFilter: (value: string) => void
  customLfdDate: string
  setCustomLfdDate: (value: string) => void
  completedTimeFilter: string
  setCompletedTimeFilter: (value: string) => void
  customCompletedDate: string
  setCustomCompletedDate: (value: string) => void
  drivers: string[]
  destinations: FacilityFilterOption[]
  importExportOptions: { value: string; label: string }[]
  statusConfig: Record<string, StatusConfig>
  shippers: NameOption[]
  orderingParties: string[]
  ports: FacilityFilterOption[]
}

const lfdFilterOptions = [
  { value: "all", label: "All LFD" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "this-week", label: "This Week" },
  { value: "overdue", label: "Overdue" },
  { value: "custom", label: "Custom Date" },
]

const completedTimeOptions = [
  { value: "today", label: "Today" },
  { value: "7days", label: "Last 7 Days" },
  { value: "30days", label: "Last 30 Days" },
  { value: "all", label: "All Time" },
  { value: "custom", label: "Custom Date" },
]

function formatLabel(item: NameOption): string {
  if (item.shortName && item.shortName !== item.name) {
    return `${item.name} (${item.shortName})`
  }
  return item.name
}

export const SearchFilters: React.FC<SearchFiltersProps> = ({
  searchTerm,
  setSearchTerm,
  selectedDriver,
  setSelectedDriver,
  selectedShipper,
  setSelectedShipper,
  selectedOrderingParty,
  setSelectedOrderingParty,
  importExportFilter,
  setImportExportFilter,
  destinationFilter,
  setDestinationFilter,
  portFilter,
  setPortFilter,
  selectedStatus,
  setSelectedStatus,
  lfdFilter,
  setLfdFilter,
  customLfdDate,
  setCustomLfdDate,
  completedTimeFilter,
  setCompletedTimeFilter,
  customCompletedDate,
  setCustomCompletedDate,
  drivers,
  destinations,
  importExportOptions,
  statusConfig,
  shippers,
  orderingParties,
  ports,
}) => {
  const [showMore, setShowMore] = useState(false)

  // Count active secondary filters
  const activeSecondaryCount = useMemo(() => {
    let count = 0
    if (selectedShipper !== 'all') count++
    if (selectedOrderingParty !== 'all') count++
    if (lfdFilter !== 'all') count++
    if (importExportFilter !== 'all') count++
    if (destinationFilter !== 'all') count++
    if (portFilter !== 'all') count++
    return count
  }, [selectedShipper, selectedOrderingParty, lfdFilter, importExportFilter, destinationFilter, portFilter])

  const clearAll = () => {
    setSearchTerm("")
    setSelectedDriver("all")
    setSelectedShipper("all")
    setSelectedOrderingParty("all")
    setSelectedStatus("all")
    setLfdFilter("all")
    setCustomLfdDate("")
    setImportExportFilter("all")
    setDestinationFilter("all")
    setPortFilter("all")
  }

  return (
    <div className="mb-3 bg-white border border-slate-200 shadow-sm rounded-xl overflow-hidden">
      {/* Primary row — always visible */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search Container / Booking # / Task ID / Shipping Line — paste several, separated by space or comma"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 h-9 text-sm bg-slate-50 border-slate-200 focus:bg-white ${searchTerm ? 'pr-9' : ''}`}
          />
          {/* Clear — only once something is typed, so the box stays quiet when empty */}
          {searchTerm && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Status */}
        <Select value={selectedStatus} onChange={setSelectedStatus} placeholder="All Status" style={{ width: 140 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
          <Select.Option value="all">All Status</Select.Option>
          {Object.entries(statusConfig).map(([status, config]) => (
            <Select.Option key={status} value={status}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${config.bgColor}`} />
                {config.label}
              </div>
            </Select.Option>
          ))}
        </Select>

        {/* Driver */}
        <Select value={selectedDriver} onChange={setSelectedDriver} placeholder="All Drivers" style={{ width: 140 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
          <Select.Option value="all">All Drivers</Select.Option>
          {drivers.map((driver, index) => (
            <Select.Option key={`driver-${index}-${driver}`} value={driver}>
              {driver}
            </Select.Option>
          ))}
        </Select>

        {/* Completed/Cancelled time range filter */}
        {(selectedStatus === 'completed' || selectedStatus === 'cancelled') && (
          <>
            <Select value={completedTimeFilter} onChange={setCompletedTimeFilter} placeholder="Time Range" style={{ width: 150 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
              {completedTimeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
            {completedTimeFilter === "custom" && (
              <Input
                type="date"
                value={customCompletedDate}
                onChange={(e) => setCustomCompletedDate(e.target.value)}
                className="w-[140px] h-9 text-sm bg-white border-slate-200"
              />
            )}
          </>
        )}

        {/* More Filters toggle */}
        <Button
          variant={showMore || activeSecondaryCount > 0 ? "default" : "outline"}
          size="sm"
          onClick={() => setShowMore(!showMore)}
          className={`h-9 text-sm gap-1.5 ${
            showMore || activeSecondaryCount > 0
              ? 'bg-[#00838F] hover:bg-[#006064] text-white'
              : 'border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          More Filters
          {activeSecondaryCount > 0 && (
            <span className="ml-0.5 bg-white/20 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {activeSecondaryCount}
            </span>
          )}
          <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showMore ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Secondary row — collapsible */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          showMore ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-wrap items-center gap-3 px-4 pb-3 pt-0 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-3 pt-3">
            {/* Shipper */}
            <Select value={selectedShipper} onChange={setSelectedShipper} placeholder="All Shippers" style={{ width: 160 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
              <Select.Option value="all">All Shippers</Select.Option>
              {shippers.map((shipper, index) => (
                <Select.Option key={`shipper-${index}-${shipper.name}`} value={shipper.name}>
                  {formatLabel(shipper)}
                </Select.Option>
              ))}
            </Select>

            {/* Ordering Party — 下单方（发邮件的下单方，与 Shipper 是两个角色） */}
            <Select
              value={selectedOrderingParty}
              onChange={setSelectedOrderingParty}
              placeholder="All Ord. Parties"
              showSearch
              optionFilterProp="value"
              style={{ width: 180 }}
              getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}
            >
              <Select.Option value="all">All Ord. Parties</Select.Option>
              {orderingParties.map((name, index) => (
                <Select.Option key={`ordparty-${index}-${name}`} value={name}>
                  {name}
                </Select.Option>
              ))}
            </Select>

            {/* LFD */}
            <Select value={lfdFilter} onChange={setLfdFilter} placeholder="All LFD" style={{ width: 140 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
              {lfdFilterOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>

            {lfdFilter === "custom" && (
              <Input
                type="date"
                value={customLfdDate}
                onChange={(e) => setCustomLfdDate(e.target.value)}
                className="w-[140px] h-9 text-sm bg-white border-slate-200"
              />
            )}

            {/* Import/Export */}
            <Select value={importExportFilter} onChange={setImportExportFilter} placeholder="All Types" style={{ width: 140 }} getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}>
              {importExportOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>

            {/* Warehouse — 送货仓库，按仓库对象筛，短名显示（与 Route 列一致） */}
            <Select
              value={destinationFilter}
              onChange={setDestinationFilter}
              placeholder="All Warehouses"
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
              getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}
            >
              <Select.Option value="all" label="All Warehouses">All Warehouses</Select.Option>
              {destinations.map((wh) => {
                const short = wh.shortName || wh.name
                const searchable = `${short} ${wh.name}`.trim()
                return (
                  <Select.Option key={`wh-${wh.id}`} value={wh.id} label={searchable}>
                    {short}
                  </Select.Option>
                )
              })}
            </Select>

            {/* Port — 起运/提柜码头，与 Destination（仓库）是两端 */}
            <Select
              value={portFilter}
              onChange={setPortFilter}
              placeholder="All Ports"
              showSearch
              optionFilterProp="label"
              style={{ width: 170 }}
              getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') as HTMLElement || trigger.closest('[role="dialog"]') as HTMLElement || document.body}
            >
              <Select.Option value="all" label="All Ports">All Ports</Select.Option>
              {ports.map((port) => {
                // Short name is the primary label (matches the Route column);
                // full name kept in the search text so typing either one hits.
                const short = port.shortName || port.name
                const searchable = `${short} ${port.name}`.trim()
                return (
                  <Select.Option key={`port-${port.id}`} value={port.id} label={searchable}>
                    {short}
                  </Select.Option>
                )
              })}
            </Select>

            {/* Clear All */}
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="h-9 text-xs text-slate-500 hover:text-slate-700"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Clear All
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
